import { Gallery, GalleryContext } from '@volkswagen-onehub/components-core';
import { type ComponentProps, type ReactNode, useContext, useEffect } from 'react';

type GalleryProps = ComponentProps<typeof Gallery>;

export type CarouselProps = {
  activeSlideIndex?: number;
  collapsed?: boolean;
  numberOfSlides?: number;
  includeBullets?: boolean;
  onSlideChange?: (activeSlideIndex: number) => void;
} & GalleryProps;

type CarouselNavigationBulletsProps = Pick<
  CarouselProps,
  'activeSlideIndex' | 'includeBullets'
> & { children?: ReactNode };

export const getCurrentSlidesPerPage = (
  slidesPerPage: GalleryProps['slidesPerPage'] = 1,
  currentWidth: number
) => {
  let currentSlidesPerPage = slidesPerPage;

  if (typeof currentSlidesPerPage === 'object') {
    const minWidthKeys = Object.keys(currentSlidesPerPage)
      .map((key: string) => +key)
      .sort((a, b) => {
        return a - b;
      })
      .reverse();

    const currentBreakpoint = minWidthKeys.find((key) => +key <= currentWidth);

    if (typeof currentBreakpoint === 'number') {
      currentSlidesPerPage = Math.floor(
        currentSlidesPerPage[currentBreakpoint.toString()]
      );
    }
  }

  return currentSlidesPerPage;
};

export const getNumberOfBullets = (currentSlidesPerPage: number, data: any[]) =>
  Math.abs(currentSlidesPerPage - data.length) + 1;

export const getBulletLabels = (
  numberOfBullets: number,
  sectionName?: string
) =>
  new Array(numberOfBullets).map(
    (i: number) => `${sectionName ? `${sectionName} - ` : ''}Slide ${i}`
  );

const CarouselNavigationBullets = ({
  activeSlideIndex = 0,
  includeBullets,
  children,
}: CarouselNavigationBulletsProps) => {
  const galleryContext = useContext(GalleryContext);

  useEffect(() => {
    galleryContext.triggerChange(activeSlideIndex);
  }, [activeSlideIndex]);

  return <>{includeBullets && children}</>;
};

const Carousel = ({
  onSlideChange,
  numberOfSlides,
  activeSlideIndex,
  children,
  bottomElements,
  includeBullets = true,
  ...restProps
}: CarouselProps) => {
  return (
    <Gallery
      bottomElements={
        <>
          <CarouselNavigationBullets
            activeSlideIndex={activeSlideIndex}
            includeBullets
          >
            {bottomElements}
          </CarouselNavigationBullets>
        </>
      }
      handleChange={onSlideChange}
      {...restProps}
    >
      {children}
    </Gallery>
  );
};

export default Carousel;
